// ── What leaves the phone, and what it weighs ────────────────────────────────
//
// A photograph taken with a phone from the last five years is between three and
// twelve megabytes. Four of them is forty megabytes over the mobile network of
// a building site, which is not slow — it is a minute of a progress bar with no
// signal behind it, and then a request that dies and loses all four.
//
// Redrawing the picture at 1600px on the long side puts it at two to five
// hundred kilobytes with no visible loss on any screen it will ever be looked
// at on, including the printed parte. That is the whole reason this exists.
//
// Two more things fall out of redrawing, and both were real bugs before it:
//
//   · EXIF orientation. A phone held upright writes the sensor's landscape
//     frame plus a tag saying "rotate this". An <img> honours the tag, a canvas
//     does not, and a thumbnail grid built from object URLs showed every
//     vertical photograph on its side. Decoding with `imageOrientation:
//     'from-image'` bakes the rotation into the pixels, so what is stored is
//     already the right way up for everybody downstream — the PDF included.
//
//   · HEIC. An iPhone stores photographs as HEIC unless told otherwise. Safari
//     decodes it and hands back a JPEG here; every other browser cannot, and
//     then the original goes up untouched — which is correct, because the
//     platform's allow-list accepts `image/heic` and the viewer shows it.
//
// ── Where it does NOT apply ──────────────────────────────────────────────────
//
// Video, PDF and anything that is not an image go through unchanged. So does
// SVG: it is a vector document, and rasterising it to a JPEG would be throwing
// away the only thing it is good for. And so does an image that came out BIGGER
// than it went in, which happens with small PNG screenshots and is the one case
// where "compressing" costs bytes instead of saving them.

const DEFAULTS = {
  // The long side. 1600 is comfortably more than any screen shows a photograph
  // at inside a card, and more than an A4 page prints one at.
  maxDim: 1600,
  quality: 0.82,
}

/** Only bitmaps, and only the ones a canvas can honestly redraw. */
function isCompressible(file) {
  const type = String(file?.type ?? '').toLowerCase()
  if (!type.startsWith('image/')) return false
  // A vector redrawn as pixels is not the same document.
  return type !== 'image/svg+xml'
}

/**
 * The picture, decoded, with its EXIF rotation already applied.
 *
 * `createImageBitmap` is the fast path and the only one that takes the
 * orientation option. The `<img>` fallback exists for Safari before 17, which
 * has neither — but an `<img>` applies the rotation on its own when it paints,
 * so drawing one to a canvas gets there too, just through a different door.
 */
async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      // A browser that has the function but not the option throws on the
      // option, not on the file. Worth one more attempt before the fallback.
      try { return await createImageBitmap(file) } catch { /* fall through */ }
    }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.decoding = 'async'
    image.src = url
    if (image.decode) await image.decode()
    else await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = reject })
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

const widthOf = (source) => source.width ?? source.naturalWidth ?? 0
const heightOf = (source) => source.height ?? source.naturalHeight ?? 0

/** The same name with a .jpg on it, because the bytes are now a JPEG. */
function asJpegName(filename) {
  const name = String(filename ?? 'photo')
  const dot = name.lastIndexOf('.')
  return `${dot > 0 ? name.slice(0, dot) : name}.jpg`
}

/**
 * One photograph, ready to upload.
 *
 * Never throws and never refuses: anything it cannot handle comes back exactly
 * as it arrived. A screen that cannot attach a photograph because the
 * compression failed is a far worse outcome than one that attaches a large one.
 *
 * @param {File|Blob} file
 * @param {{maxDim?: number, quality?: number}} [options]
 * @returns {Promise<File|Blob>} a JPEG, or the original
 */
export async function compressImage(file, options = {}) {
  const { maxDim, quality } = { ...DEFAULTS, ...options }

  if (!(file instanceof Blob) || !isCompressible(file)) return file
  if (typeof document === 'undefined') return file

  try {
    const source = await decode(file)
    const width = widthOf(source)
    const height = heightOf(source)
    if (!width || !height) return file

    const scale = Math.min(1, maxDim / Math.max(width, height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(width * scale)
    canvas.height = Math.round(height * scale)

    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(source, 0, 0, canvas.width, canvas.height)
    // A bitmap holds its pixels until it is told not to; a grid of ten photos
    // would otherwise keep ten decoded frames alive for as long as the form is.
    source.close?.()

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality))
    if (!blob) return file

    // The one case where this costs bytes instead of saving them: a small PNG
    // screenshot re-encoded as a JPEG is usually larger, and always uglier.
    if (blob.size >= file.size && scale === 1) return file

    return new File([blob], asJpegName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified ?? Date.now(),
    })
  } catch {
    // A browser that could not decode its own camera's output is not a reason
    // to lose what somebody photographed.
    return file
  }
}

/**
 * A handful of them, in order.
 *
 * Sequentially and not with `Promise.all`: decoding ten twelve-megapixel
 * photographs at once is ten full-size bitmaps in memory, which is how a phone
 * browser reloads the tab in the middle of writing a parte.
 *
 * @param {Iterable<File|Blob>} files
 * @param {{maxDim?: number, quality?: number}} [options]
 * @returns {Promise<Array<File|Blob>>}
 */
export async function compressImages(files, options = {}) {
  const out = []
  for (const file of files ?? []) out.push(await compressImage(file, options))
  return out
}

export const COMPRESSION_DEFAULTS = DEFAULTS
